import { useEffect, useState } from 'react'
import { Button, Modal, useToast } from '@/design/components'
import { ApiError } from '@/lib/api'
import { todayIso } from '../format'
import { useCaseOptions, useCreateTimeEntry } from '../hooks'
import type { TimeEntryFormState } from './TimeEntryFormFields'
import { TimeEntryFormFields, combineMinutes } from './TimeEntryFormFields'

export interface NewTimeEntryModalProps {
  open: boolean
  onClose: () => void
  /** Prefill z karty sprawy (`/czas?sprawa=<id>&nowa=1`). */
  initialCaseId?: number | null
}

function emptyForm(initialCaseId?: number | null): TimeEntryFormState {
  return {
    caseId: initialCaseId ? String(initialCaseId) : '',
    workDate: todayIso(),
    hours: '',
    minutes: '',
    description: '',
    billable: true,
  }
}

/** „Rejestruj czas" — nowy wpis, zawsze na zalogowanego użytkownika. */
export function NewTimeEntryModal({ open, onClose, initialCaseId }: NewTimeEntryModalProps) {
  const toast = useToast()
  const cases = useCaseOptions(open)
  const create = useCreateTimeEntry()

  const [form, setForm] = useState<TimeEntryFormState>(() => emptyForm(initialCaseId))
  const [error, setError] = useState<string | null>(null)

  // Prefill sprawy przychodzi asynchronicznie z query param — odśwież formularz po otwarciu.
  useEffect(() => {
    if (open) {
      setForm(emptyForm(initialCaseId))
      setError(null)
    }
  }, [open, initialCaseId])

  const submit = async () => {
    setError(null)
    const minutes = combineMinutes(form.hours, form.minutes)
    if (!form.caseId) {
      setError('Wybierz sprawę.')
      return
    }
    if (!form.workDate) {
      setError('Podaj datę.')
      return
    }
    if (minutes === null) {
      setError('Podaj czas w zakresie od 1 minuty do 24 godzin.')
      return
    }
    try {
      await create.mutateAsync({
        case_id: Number(form.caseId),
        work_date: form.workDate,
        minutes,
        description: form.description.trim() ? form.description.trim() : null,
        billable: form.billable,
      })
      toast.success('Czas zarejestrowany')
      onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.userMessage : 'Nie udało się zapisać wpisu.')
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Rejestruj czas"
      subtitle="Wpis trafia na Twoje konto — czas loguje się wyłącznie na siebie."
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={create.isPending}>
            Anuluj
          </Button>
          <Button variant="primary" onClick={submit} loading={create.isPending}>
            Zapisz wpis
          </Button>
        </>
      }
    >
      <TimeEntryFormFields form={form} onChange={setForm} cases={cases.data?.items ?? []} />
      {error ? <p className="mt-3 text-micro text-danger">{error}</p> : null}
    </Modal>
  )
}
